import { isMailUuid } from '../utils/mailNavigation'
import api from '../api/axios'

const ROOT = 'api/emails/v1/mailboxes/'

export interface Mailbox {
  id: string
  name: string
  address: string
  owner: number
  visibility: 'personal' | 'shared'
  provider: 'generic' | 'gmail'
  auth_kind: 'password' | 'oauth2'
  username: string
  imap_host: string
  imap_port: number
  imap_security: 'tls' | 'starttls'
  smtp_host: string
  smtp_port: number
  smtp_security: 'tls' | 'starttls'
  folders: string[]
  initial_since: string | null
  max_messages_per_run: number
  max_message_bytes: number
  max_attachment_bytes: number
  ai_indexing_enabled: boolean
  readonly ai_indexing_effective: boolean
  readonly ai_indexing_blocked_reason: MailIndexingBlockedReason
  sync_interval_seconds: number
  timeout_seconds: number
  sent_folder: string
  config_version: number
  last_sync_at: string | null
  next_sync_at: string | null
  connection_error: string
  has_credentials: boolean
  permissions: Record<'mail.read' | 'mail.sync' | 'mail.draft' | 'mail.send' | 'mail.manage', boolean>
}

export interface MailRun {
  id: string
  status: 'queued' | 'running' | 'succeeded' | 'partial' | 'failed' | 'interrupted'
  created_at: string
  started_at: string | null
  finished_at: string | null
  acquired_count: number
  partial_count: number
  replayed_count: number
  error_code: string
  diagnostics: Record<string, unknown>
}

export interface Page<T> { count: number; next: string | null; previous: string | null; results: T[] }
export type MailboxInput = Pick<Mailbox, 'ai_indexing_enabled' | 'name' | 'address' | 'visibility' | 'provider' | 'auth_kind' | 'username' | 'imap_host' | 'imap_port' | 'imap_security' | 'smtp_host' | 'smtp_port' | 'smtp_security' | 'folders' | 'initial_since' | 'max_messages_per_run' | 'max_message_bytes' | 'max_attachment_bytes' | 'sync_interval_seconds' | 'timeout_seconds' | 'sent_folder'> & { password?: string; config_version?: number }

export class MailApiError extends Error {
  constructor(public status: number, public code: string, public fields: Record<string, string>, message: string, public detail?: unknown) { super(message) }
}

function asError(error: any): never {
  const payload = error?.response?.data?.error
  const detail = payload?.detail
  const fields: Record<string, string> = {}
  if (detail && typeof detail === 'object') {
    Object.entries(detail).forEach(([key, value]) => { fields[key] = Array.isArray(value) ? String(value[0]) : String(value) })
  }
  throw new MailApiError(error?.response?.status || 0, payload?.code || 'network_error', fields,
    typeof detail === 'string' ? detail : 'Operazione non riuscita. Verifica la configurazione e riprova.', detail)
}

async function request<T>(work: Promise<{ data: T }>): Promise<T> { try { return (await work).data } catch (error) { return asError(error) } }
export const listMailboxes = () => request<Page<Mailbox>>(api.get(ROOT, { params: { page_size: 100 } }))
export const getMailbox = (id: string) => request<Mailbox>(api.get(`${ROOT}${id}/`))
export const createMailbox = (input: MailboxInput) => request<Mailbox>(api.post(ROOT, input))
export const patchMailbox = (id: string, input: Partial<MailboxInput> & { config_version: number }) => request<Mailbox>(api.patch(`${ROOT}${id}/`, input))
export const listMailRuns = (id: string) => request<Page<MailRun>>(api.get(`${ROOT}${id}/runs/`, { params: { page_size: 10 } }))
export const syncMailbox = (id: string) => request<Pick<MailRun, 'id' | 'status' | 'error_code'>>(api.post(`${ROOT}${id}/sync/`))
export const startMailOAuth = (id: string) => request<{ authorization_url: string }>(api.post(`${ROOT}${id}/oauth/start/`))
export const completeMailOAuth = (code: string, state: string) => request<Mailbox>(api.post(`${ROOT}oauth/callback/`, { code, state }))

const MESSAGES = 'api/emails/v1/messages/'
const ATTACHMENTS = 'api/emails/v1/attachments/'

export interface MailMessage {
  id: string; mailbox: string; message_id: string; in_reply_to: string; subject: string
  sender: MailAddress[]; to: MailAddress[]; cc: MailAddress[]; sent_at: string | null; received_at: string
  availability: 'complete' | 'partial' | 'metadata_only' | 'expired'
  incomplete_reasons: string[]; seen: boolean | null
}
export type MailAddress = string | { name: string; address: string }
export const addressValue = (item: MailAddress) => typeof item === 'string' ? item : item.address
export const addressLabel = (item: MailAddress) => typeof item === 'string' ? item : item.name ? `${item.name} <${item.address}>` : item.address
export interface MailPresence {
  id: string; folder: string; generation: number; uid_validity: number; uid: number
  flags: string[]; is_present: boolean; last_seen_at: string | null
}
export interface MailAttachment {
  id: string; part_index: number; filename: string; content_type: string; content_id: string
  size_bytes: number; sha256: string; availability: string; excluded_reason: string
}
export interface MailMessageDetail extends MailMessage {
  logical_message_id?: string | null; thread_id?: string | null; reconciliation_status?: string
  references: string[]; body_text: string; source_sha256: string; parser_version: number
  expired_at: string | null; remote_presences: MailPresence[]; attachments: MailAttachment[]
}
export interface MailThread { messages: MailMessage[]; truncated: boolean }
export interface MailFlagsResult {
  seen: boolean; status: 'succeeded' | 'partial' | 'failed'
  results: Array<{ presence_id: string; status: string; flags?: string[]; error_code?: string }>
}
export interface MessageFilters { mailbox: string; folder?: string; seen?: boolean; q?: string; page: number; page_size: number }

export const listMailMessages = (filters: MessageFilters) => request<Page<MailMessage>>(api.get(MESSAGES, { params: filters }))
export const getMailMessage = (id: string) => request<MailMessageDetail>(api.get(`${MESSAGES}${id}/`))
export const getMailThread = (id: string) => request<MailThread>(api.get(`${MESSAGES}${id}/thread/`))
export const setMailSeen = (id: string, seen: boolean) => request<MailFlagsResult>(api.patch(`${MESSAGES}${id}/flags/`, { seen }))
export const saveMailAttachment = (id: string, space_id: number, parent_id: number | null) =>
  request<{ id: number; created: boolean }>(api.post(`${ATTACHMENTS}${id}/save-document/`, { space_id, parent_id }))

const DRAFTS = 'api/emails/v1/drafts/'
const SENDS = 'api/emails/v1/sends/'
export type DraftMode = 'new' | 'reply' | 'forward'
export interface DraftContent {
  to: string[]; cc: string[]; bcc: string[]; subject: string; body_text: string
  document_ids: number[]; attachment_ids: string[]; reply_to: string | null; mode: DraftMode
}
export interface MailDraft extends DraftContent {
  id: string; mailbox: string; version: number; status: 'draft' | 'confirmed'; send_id: string | null
  source_availability: string | null; source_incomplete_reasons: string[] | null
}
export interface MailSend {
  id: string; draft: string; status: 'queued' | 'sending' | 'accepted' | 'partial' | 'failed' | 'uncertain' | 'cancelled'
  draft_version: number; sent_copy_status: string; accepted_recipients: string[]; rejected_recipients: Record<string, string> | string[]
  error_code: string; resolution: string; resolution_evidence: string; local_message: string | null
}
export const listMailDrafts = (page = 1, page_size = 100) => request<Page<MailDraft>>(api.get(DRAFTS, { params: { page, page_size } }))
export const getMailDraft = (id: string) => request<MailDraft>(api.get(`${DRAFTS}${id}/`))
// A loaded draft also contains server metadata. Send only editable fields.
const draftInput = ({ to, cc, bcc, subject, body_text, document_ids, attachment_ids, reply_to, mode }: DraftContent): DraftContent =>
  ({ to, cc, bcc, subject, body_text, document_ids, attachment_ids, reply_to: reply_to ?? null, mode })
export const createMailDraft = (mailbox: string, content: DraftContent) => request<MailDraft>(api.post(DRAFTS, { mailbox, ...draftInput(content) }))
export const updateMailDraft = (id: string, version: number, content: DraftContent) => request<MailDraft>(api.patch(`${DRAFTS}${id}/`, { ...draftInput(content), version }))
export const confirmMailDraft = (id: string, version: number, idempotency_key: string) =>
  request<MailSend>(api.post(`${DRAFTS}${id}/confirm/`, { version, idempotency_key }))
export const getMailSend = (id: string) => request<MailSend>(api.get(`${SENDS}${id}/`))
export const resolveMailSend = (id: string, resolution: 'accepted' | 'rejected', evidence: string) =>
  request<Pick<MailSend, 'id' | 'status' | 'resolution' | 'resolution_evidence'>>(api.post(`${SENDS}${id}/resolve/`, { resolution, evidence }))

export async function downloadMailFile(kind: 'source' | 'attachment', id: string, filename: string): Promise<void> {
  const url = kind === 'source' ? `${MESSAGES}${id}/source/` : `${ATTACHMENTS}${id}/download/`
  try {
    const { data } = await api.get<Blob>(url, { responseType: 'blob' })
    const objectUrl = URL.createObjectURL(data)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename || (kind === 'source' ? 'messaggio.eml' : 'allegato')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
  } catch (error) { return asError(error) }
}

export type LogicalMailAttachment = Pick<MailAttachment, 'id' | 'filename' | 'content_type' | 'content_id' | 'size_bytes' | 'availability' | 'excluded_reason' | 'part_index'>
export interface LogicalMailSummary {
  id: string; source_id: string; sort_at: string; subject: string; sender: MailAddress[]; to: MailAddress[]; cc: MailAddress[]
  availability: MailMessage['availability']; incomplete_reasons: string[]; attachments: LogicalMailAttachment[]
}
export interface LogicalMailDetail extends LogicalMailSummary { body_text: string }
export interface LogicalMailThreadPage {
  id: string; requested_id: string; mailbox_id: string; revision: number; state: string; anchor: string
  messages: LogicalMailSummary[]; previous_cursor: string | null; next_cursor: string | null; has_more_before: boolean; has_more_after: boolean
}
export interface LogicalMailThreadQuery { mailbox: string; anchor?: string; cursor?: string; revision?: number; limit?: 50 }
const LOGICAL = 'api/emails/v1/logical-messages/'
export function getMailLogicalThread(id: string, query: LogicalMailThreadQuery): Promise<LogicalMailThreadPage> {
  if (!isMailUuid(id) || !isMailUuid(query.mailbox) || (query.anchor && !isMailUuid(query.anchor)) || (query.cursor && query.anchor) || (query.cursor && (!Number.isInteger(query.revision) || query.revision < 1))) return Promise.reject(new Error('Contesto conversazione non valido.'))
  const params = query.cursor ? { mailbox: query.mailbox, cursor: query.cursor, revision: query.revision, limit: 50 } : { mailbox: query.mailbox, anchor: query.anchor, limit: 50 }
  return request<LogicalMailThreadPage>(api.get(`api/emails/v1/threads/${id}/`, { params }))
}
export function getMailLogicalMessage(id: string, mailbox: string): Promise<LogicalMailDetail> {
  if (!isMailUuid(id) || !isMailUuid(mailbox)) return Promise.reject(new Error('Identità messaggio non valida.'))
  return request<LogicalMailDetail>(api.get(`${LOGICAL}${id}/`, { params: { mailbox } }))
}
export function getMailLogicalMessages(mailbox: string, ids: string[]): Promise<{ messages: LogicalMailDetail[] }> {
  if (!isMailUuid(mailbox) || ids.length < 1 || ids.length > 20 || new Set(ids).size !== ids.length || !ids.every(isMailUuid)) return Promise.reject(new Error('Seleziona da 1 a 20 UUID logici distinti.'))
  return request<{ messages: LogicalMailDetail[] }>(api.post(`${LOGICAL}batch/`, { mailbox, ids }))
}

export type MailIndexingBlockedReason = 'global_disabled' | 'account_disabled' | null
export interface MailIndexBatchStatus {
  id: string
  enumeration_status: 'acquiring' | 'pending' | 'running' | 'paused' | 'complete' | 'failed'
  after_id: number
  high_water_mark: number
  enumeration_counters: Record<string, number>
  current_requests: Partial<Record<'pending' | 'processing' | 'retry_wait' | 'paused' | 'ready' | 'failed' | 'unavailable', number>>
  scope_eligible: number
  scope_ready: number
  scope_to_process: number
  remaining_to_enumerate: number
  settled: boolean
}
export interface MailIndexHistoryAction {
  kind: 'start' | 'resume' | 'existing' | 'none'
  batch_id: string | null
  high_water_mark: number
  eligible: number
  ready: number
  to_process: number
  allowed: boolean
}
export type MailIndexHistoryInput = { expected_action?: Omit<MailIndexHistoryAction, 'allowed'> }
export type MailIndexHistoryResult =
  | (MailIndexBatchStatus & { action: 'start' | 'resume' | 'existing' })
  | { action: 'none'; id: null; batch: null }
export interface MailIndexingStatus {
  enabled: boolean
  effective: boolean
  blocked_reason: MailIndexingBlockedReason
  eligible: number
  ready: number
  to_process: number
  history_action: MailIndexHistoryAction
  batches: MailIndexBatchStatus[]
}
export const getMailboxIndexing = (id: string) => request<MailIndexingStatus>(api.get(`${ROOT}${id}/indexing/`))
export const startMailboxIndexing = (id: string, input: MailIndexHistoryInput = {}) => request<MailIndexHistoryResult>(api.post(`${ROOT}${id}/indexing/`, input))
